import { Users, Search, Terminal, Image, Network } from 'lucide-react';
import { MainBodyData, KeyTakeawaysData, InvestigationQuestionsType } from './types';

export const socmintContent: MainBodyData = {
  number: '01',
  title: 'Social Media Intelligence (SOCMINT)',
  icon: Users,
  sections: {
    overview: `Social Media Intelligence (SOCMINT) is a subset of OSINT, it refers to gathering information on a subject through the use of their social media accounts.
    Social media platforms are gold mines in terms of personal or private information. 
    Investigators are able to analyse potential connections, locations, habits, and other related data through social media mapping and profiling techniques.`,
    
    note: "Exploiting and unauthorised attempts to gain access to a system is illegal. Computer Misuse Acts apply.",
    steps: [
      { title: "Profile Discovery", description: "Identify target accounts across multiple platforms (Sherlock CLI)" },
      { title: "Data Collection", description: "Archive posts, connections, and metadata" },
      { title: "Relationship Mapping", description: "Document connections and associations." },
      { title: "Timeline Analysis", description: "Create chronological activity records, events, parties." },
      { title: "Content Analysis", description: "Analyse posts for sentiment, topics, and behavioral patterns. Was there a behavioral shift?" }
    ],
    platforms: ["Twitter/X", "Facebook", "LinkedIn", "Instagram", "TikTok", "Reddit"],
    dataPoints: [
      "Username and display names",
      "Profile creation dates",
      "Location information",
      "Contact details",
      "Post frequency patterns",
      "Friend connections"
    ]
  }
};

export const socmintTakeaways: KeyTakeawaysData = {
  sectionTitle: "SOCMINT",
  takeaways: [
    "Always verify information from multiple social platforms",
    "Document the complete chain of custody for evidence",
    "Respect privacy laws and platform terms of service",
    "Use archived snapshots to preserve temporal data",
    "Map social connections to identify networks",
    "Cross-reference timestamps across platforms"
  ],
  tools: [
    { name: "Sherlock", category: "USERNAME SEARCH", description: "Hunt down social media accounts across 300+ platforms", url: "github.com/sherlock-project", icon: Search },
    { name: "Social Mapper", category: "FACIAL RECOGNITION", description: "Correlate social media profiles using facial recognition", url: "github.com/Greenwolf", icon: Users },
    { name: "Twint", category: "TWITTER OSINT", description: "Advanced Twitter scraping without API limits", url: "github.com/twintproject", icon: Terminal },
    { name: "InstaLooter", category: "INSTAGRAM", description: "Download Instagram content and metadata", url: "github.com/althonos", icon: Image },
    { name: "Maltego", category: "LINK ANALYSIS", description: "Visualize relationships and connections", url: "maltego.com", icon: Network },
    { name: "Social-Analyzer", category: "PROFILING", description: "Analyze and find social media accounts", url: "github.com/qeeqbox", icon: Users }
  ]
};

export const socmintInvestigationQuestions: InvestigationQuestionsType = {
  profileDiscovery: {
    title: 'Profile Discovery',
    questions: [
      { id: 'primary_account', text: 'Have you identified the primary account across major platforms?' },
      { id: 'alt_accounts', text: 'Are there any alternate or secondary accounts?' },
      { id: 'usernames', text: 'Have you documented all username variations?' },
      { id: 'deactivated', text: 'Were any previously active accounts deactivated or deleted?' }
    ]
  },
  dataCollection: {
    title: 'Data Collection & Archiving',
    questions: [
      { id: 'screenshots', text: 'Have all posts been screenshotted/archived?' },
      { id: 'metadata', text: 'Was metadata (timestamps, location) preserved?' },
      { id: 'bio_changes', text: 'Have bio/profile description changes been documented?' },
      { id: 'media_downloaded', text: 'Were all media files downloaded and backed up?' },
      { id: 'chain_custody', text: 'Has chain of custody been established for all evidence?' }
    ]
  },
  relationshipMapping: {
    title: 'Relationship Mapping',
    questions: [
      { id: 'connections_identified', text: 'Have all significant connections been identified?' },
      { id: 'mutual_connections', text: 'Have mutual connections been cross-referenced?' },
      { id: 'network_diagram', text: 'Has a relationship network diagram been created?' },
      { id: 'group_memberships', text: 'Have group memberships and communities been documented?' },
      { id: 'follower_analysis', text: 'Has follower/subscriber growth been analyzed?' }
    ]
  },
  timelineAnalysis: {
    title: 'Timeline & Activity Analysis',
    questions: [
      { id: 'activity_timeline', text: 'Has a chronological activity timeline been created?' },
      { id: 'posting_patterns', text: 'Have posting patterns and frequency been analyzed?' },
      { id: 'location_timeline', text: 'Have locations been geotagged and mapped?' },
      { id: 'event_correlation', text: 'Have posts been correlated with known events?' },
      { id: 'time_zones', text: 'Have time zone patterns been analyzed?' }
    ]
  },
  contentAnalysis: {
    title: 'Content & Behavioral Analysis',
    questions: [
      { id: 'sentiment_analysis', text: 'Has sentiment and tone of posts been analyzed?' },
      { id: 'topic_trends', text: 'Have topic trends and interests been identified?' },
      { id: 'behavioral_shift', text: 'Have there been any significant behavioral or content shifts?' },
      { id: 'engagement_patterns', text: 'Have engagement patterns (likes, comments, shares) been noted?' },
      { id: 'language_analysis', text: 'Has linguistic style and language patterns been analyzed?' }
    ]
  },
  platformSpecific: {
    title: 'Platform-Specific Intelligence',
    questions: [
      { id: 'twitter_threads', text: 'Twitter/X: Have conversation threads been preserved?' },
      { id: 'facebook_details', text: 'Facebook: Have friend lists and private details been documented?' },
      { id: 'linkedin_connections', text: 'LinkedIn: Have professional connections and history been reviewed?' },
      { id: 'instagram_followers', text: 'Instagram: Has follower list and story archives been obtained?' },
      { id: 'tiktok_content', text: 'TikTok: Have videos and engagement metrics been preserved?' }
    ]
  },
  verification: {
    title: 'Verification & Validation',
    questions: [
      { id: 'cross_platform', text: 'Has information been cross-verified across multiple platforms?' },
      { id: 'external_sources', text: 'Have findings been correlated with external data sources?' },
      { id: 'contact_validation', text: 'Have contact details been validated independently?' },
      { id: 'timeline_accuracy', text: 'Has the timeline been verified for accuracy and gaps?' },
      { id: 'conflicts', text: 'Have any contradictions or discrepancies been noted?' }
    ]
  },
  legalCompliance: {
    title: 'Legal & Ethical Compliance',
    questions: [
      { id: 'public_info', text: 'Is all collected data from publicly available sources?' },
      { id: 'terms_of_service', text: 'Has platform Terms of Service been reviewed?' },
      { id: 'privacy_laws', text: 'Have relevant privacy laws (GDPR, local laws) been considered?' },
      { id: 'authorization', text: 'Is there proper authorization for this investigation?' },
      { id: 'documentation', text: 'Has methodology and authorization been fully documented?' }
    ]
  }
};